import React from "react";
import {
  Badge,
  Box,
  IconButton,
  styled,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Star as StarIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from "@mui/icons-material";
import { WorkItemCategory, getCategoryColor, getCategoryIcon } from "../../../utils/workItemUtils";
const StyledTab = styled(Tab)(() => ({
  fontSize: "0.9rem",
  minHeight: 48,
  fontWeight: 500,
  "&.Mui-selected": {
    fontWeight: 600,
  },
}));

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: -8,
    top: 8,
  },
}));

interface ActivityTabsProps {
  tabValue: string;
  onTabChange: (event: React.SyntheticEvent, newValue: string) => void;
  categoryCounts: Record<string, number>;
  viewType: "card" | "list";
  onViewTypeChange: (viewType: "card" | "list") => void;
}

const ActivityTabs: React.FC<ActivityTabsProps> = ({
  tabValue,
  onTabChange,
  categoryCounts,
  viewType,
  onViewTypeChange,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <Tabs
        value={tabValue}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          "& .MuiTabs-flexContainer": {
            borderBottom: 1,
            borderColor: "divider",
          },
        }}
      >
        {/* Alla aktiviteter */}
        <StyledTab
          label={
            <StyledBadge
              badgeContent={categoryCounts["all"]}
              color="primary"
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MenuIcon fontSize="small" sx={{ mr: 1 }} />
                Alla
              </Box>
            </StyledBadge>
          }
          value="all"
        />

        {/* Kategorier */}
        {Object.values(WorkItemCategory).map((category) => (
          <StyledTab
            key={category}
            label={
              <StyledBadge
                badgeContent={categoryCounts[category]}
                color="primary"
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    component="span"
                    sx={{
                      color: getCategoryColor(category as WorkItemCategory),
                      display: "flex",
                      alignItems: "center",
                      mr: 1,
                    }}
                  >
                    {getCategoryIcon(category as WorkItemCategory)}
                  </Box>
                  {category}
                </Box>
              </StyledBadge>
            }
            value={category}
            sx={{
              color:
                tabValue === category
                  ? getCategoryColor(category as WorkItemCategory)
                  : "inherit",
            }}
          />
        ))}

        {/* Favoriter */}
        <StyledTab
          label={
            <StyledBadge
              badgeContent={categoryCounts["favorites"]}
              color="primary"
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <StarIcon 
                  fontSize="small" 
                  sx={{ 
                    mr: 1,
                    color: tabValue === "favorites" ? "#f50057" : "inherit"
                  }} 
                />
                Favoriter
              </Box>
            </StyledBadge>
          }
          value="favorites"
          sx={{
            color: tabValue === "favorites" ? "#f50057" : "inherit",
          }}
        />
      </Tabs>

      {/* View type controls */}
      <Box
        sx={{ 
          ml: 2, 
          display: "flex", 
          alignItems: "center", 
          gap: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 0.5
        }}
      >
        <Tooltip title="Kortvy">
          <IconButton
            size="small"
            color={viewType === "card" ? "primary" : "default"}
            onClick={() => onViewTypeChange("card")}
            sx={{
              borderRadius: 1,
            }}
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Listvy">
          <IconButton
            size="small"
            color={viewType === "list" ? "primary" : "default"}
            onClick={() => onViewTypeChange("list")}
            sx={{
              borderRadius: 1,
            }}
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ActivityTabs;